import { QueryClient, QueryCache, MutationCache, keepPreviousData } from "@tanstack/react-query";
import { ApiError } from "~/shared/api/client";

let isSigningOut = false;

// Only 403 from the /users/me query means "user is BLOCKED/DELETED" —
// that's the one case where React Query onError should trigger a sign-out.
//
// 401 is NOT handled here on purpose: baseFetch throws ApiError(401) with
// "No valid token" whenever a public page (landing, pricing) fires a query
// without a token — that's normal, not a dead session. Real server 401s
// (token rejected by backend after a real fetch) are handled by
// handleAuthError in api/client.ts, which runs inside the response branch
// and correctly distinguishes "no token" (throw before fetch) from
// "server rejected token" (response.status === 401).
function shouldLogoutOn(error: unknown, isMeQuery: boolean): boolean {
    if (!(error instanceof ApiError)) return false;
    return error.code === "FORBIDDEN" && isMeQuery;
}

function triggerSignOut(): void {
    if (isSigningOut) return;
    isSigningOut = true;
    void import("~/shared/lib/auth/auth-provider").then((mod) => mod.signOut({ callbackUrl: "/" }));
}

function isUserMeQueryKey(key: readonly unknown[] | undefined): boolean {
    return key?.[0] === "user" && key?.[1] === "me";
}

export const queryClient = new QueryClient({
    queryCache: new QueryCache({
        onError: (error, query) => {
            if (shouldLogoutOn(error, isUserMeQueryKey(query.queryKey))) {
                triggerSignOut();
            }
        },
    }),
    mutationCache: new MutationCache({
        onError: (error, _vars, _ctx, mutation) => {
            // /users/me is fetched via useCurrentUser (query), not mutation;
            // mutationKey-based match is just a safety fallback.
            if (shouldLogoutOn(error, isUserMeQueryKey(mutation.options.mutationKey))) {
                triggerSignOut();
            }
        },
    }),
    defaultOptions: {
        queries: {
            retry: (count, error) => {
                if (error instanceof ApiError && error.code === "FORBIDDEN") return false;
                return count < 3;
            },
            placeholderData: keepPreviousData,
        },
    },
});
